/**
 * ListCompactView - Dense text listing of list entries
 *
 * No steppers by design; clicking a card name opens the details panel for editing.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import SplitManaCost from '@/components/ManaSymbol/SplitManaCost';
import useUserSettings from '@/features/settings/hooks/api';
import type { CardGroup } from '@/lib/cardGrouping';
import { getDisplayManaCost, getRarityTextClass } from '@/lib/cardUtils';
import { formatPrice } from '@/lib/price';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { EnrichedListCard } from '@/types/lists';

export interface ListCompactViewProps {
  groups: CardGroup<EnrichedListCard>[];
  showGroupHeaders: boolean;
  onOpenDetails: (card: EnrichedListCard) => void;
}

export default function ListCompactView({ groups, showGroupHeaders, onOpenDetails }: ListCompactViewProps): ReactElement {
  return (
    <div className="columns-1 gap-8 lg:columns-2 xl:columns-3">
      {groups.map((group) => (
        <section key={group.key} className="mb-6 break-inside-avoid">
          {showGroupHeaders && (
            <h3 className="mb-1 border-b border-zinc-800 pb-1 text-xs font-medium tracking-wide text-zinc-400 uppercase">
              {group.label} <span className="text-zinc-600">({group.cards.length})</span>
            </h3>
          )}
          <table className="w-full table-auto">
            <tbody>
              {group.cards.map((card) => (
                <CompactRow key={card.id} card={card} onOpenDetails={() => onOpenDetails(card)} />
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

interface CompactRowProps {
  card: EnrichedListCard;
  onOpenDetails: () => void;
}

function CompactRow({ card, onOpenDetails }: CompactRowProps): ReactElement {
  const { settings } = useUserSettings();
  const fullCard = useAtomValue(fullCardCacheAtom).get(card.scryfall_id);
  const manaCost = fullCard ? getDisplayManaCost(fullCard) : null;

  return (
    <tr className="text-sm">
      <td className={clsx('py-0.5 pr-1.5 text-right font-mono tabular-nums', card.quantity === 1 ? 'text-zinc-600' : 'text-zinc-400')}>
        {card.quantity}
      </td>

      <td className="max-w-0 truncate py-0.5 pr-2 lg:max-w-none">
        <CardHoverPreview imageUris={card.sample_image_uris} name={card.name} layout={fullCard?.layout} cardFaces={fullCard?.card_faces}>
          <button
            type="button"
            onClick={onOpenDetails}
            className={clsx(
              'hover:text-aether focus-visible:ring-aether block max-w-full truncate rounded text-left transition-colors focus:outline-none focus-visible:ring-2',
              fullCard ? getRarityTextClass(fullCard.rarity) : 'text-zinc-300',
            )}
          >
            {card.name}
          </button>
        </CardHoverPreview>
      </td>

      <td className="py-0.5 pr-2 text-right whitespace-nowrap">{manaCost ? <SplitManaCost cost={manaCost} shadow={false} /> : null}</td>

      <td
        className={clsx(
          'py-0.5 pr-2 text-right font-mono text-xs whitespace-nowrap tabular-nums',
          card.available_quantity > 0 ? 'text-emerald-400' : 'text-zinc-600',
        )}
        title="Copies available"
      >
        {card.available_quantity}
      </td>

      <td className="py-0.5 text-right font-mono text-xs whitespace-nowrap text-zinc-500 tabular-nums">
        {card.total_price != null ? formatPrice(card.total_price, settings.currency) : '—'}
      </td>
    </tr>
  );
}
